
import { ReactNode, useState } from 'react'
import { InView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import { useIsAdmin } from '../hooks/isAdmin'
import { useIsCourseAdmin } from '../hooks/isCourseAdmin'
import { useIsCenterManager } from '../hooks/isCenterManager'
import { useIsContentReviewer } from '../hooks/isContentReviewer'
import { useIsTeacher } from '../hooks/isTeacher'
import {
  Container,
  Dropdown,
  Icon,
  Menu,
  Segment,
  Sidebar,
} from 'semantic-ui-react'

import { PATHS } from '../paths';

import './ResponsiveContainer.css'

import { MediaContextProvider, Media } from '../mobile'

// TODO: add an alerts menu item here
const SharedMenu = ({ variant }: { variant: 'desktop' | 'mobile' }) => {
  const isAdmin = useIsAdmin();
  const isCourseAdmin = useIsCourseAdmin();
  const isCenterManager = useIsCenterManager();
  const isContentReviewer = useIsContentReviewer();
  const location = useLocation();

  // The "Create" menu groups the create-new actions. Each item is role-gated;
  // the group itself shows if the viewer can create at least one thing.
  const canCreatePerson = isAdmin || isCenterManager;
  const showCreateMenu = isAdmin || isCenterManager;

  return (
    <>
      <Menu.Item as='a' href={PATHS.CLASSES} active={location.pathname === PATHS.CLASSES}>
        Classes
      </Menu.Item>
      <Menu.Item as='a' href={PATHS.PEOPLE} active={location.pathname === PATHS.PEOPLE}>
        People
      </Menu.Item>

      {showCreateMenu && (variant === 'desktop' ? (
        <Dropdown item trigger={<span><Icon name='plus' /> Create</span>} icon='dropdown'>
          <Dropdown.Menu>
            {isAdmin && <Dropdown.Item as='a' href={PATHS.CLASS_CREATE}>Create class</Dropdown.Item>}
            {canCreatePerson && <Dropdown.Item as='a' href={PATHS.PEOPLE_CREATE}>Create person</Dropdown.Item>}
            {isAdmin && <Dropdown.Item as='a' href={PATHS.TAGS}>Create tag</Dropdown.Item>}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        <>
          {isAdmin && <Menu.Item as='a' href={PATHS.CLASS_CREATE} active={location.pathname === PATHS.CLASS_CREATE}>
            Create class
          </Menu.Item>}
          {canCreatePerson && <Menu.Item as='a' href={PATHS.PEOPLE_CREATE} active={location.pathname === PATHS.PEOPLE_CREATE}>
            Create person
          </Menu.Item>}
          {isAdmin && <Menu.Item as='a' href={PATHS.TAGS} active={location.pathname === PATHS.TAGS}>
            Create tag
          </Menu.Item>}
        </>
      ))}
      {isAdmin && <Menu.Item as='a' href={PATHS.ADMIN_TOOLS} active={location.pathname === PATHS.ADMIN_TOOLS}>
        Admin
      </Menu.Item>}

      {/* Content review tools — content reviewers only, and only on desktop. */}
      {isContentReviewer && variant === 'desktop' && (
        <Dropdown item trigger={<span><Icon name='edit outline' /> Content</span>} icon='dropdown'>
          <Dropdown.Menu>
            <Dropdown.Item as='a' href={PATHS.POSTS_CREATE}>Create post</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.POST_REVIEWS}>Review posts</Dropdown.Item>
            <Dropdown.Item as='a' href={PATHS.GALLERY}>Gallery</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      )}
      {isCourseAdmin && <Menu.Item as='a' href={PATHS.DASHBOARD} active={location.pathname === PATHS.DASHBOARD}>
        Dashboard
      </Menu.Item>}
      {(isCourseAdmin || isCenterManager) && <Menu.Item as='a' href={PATHS.ATTENDANCE} active={location.pathname === PATHS.ATTENDANCE}>
        Attendance
      </Menu.Item>}
      {/* <Menu.Item as='a'>Work</Menu.Item> */}

      {variant === 'desktop' ? (
        <Menu.Menu position='right'>
          <Dropdown item trigger={<Icon name='user' />} icon='dropdown'>
            <Dropdown.Menu>
              {isAdmin && (
                <Dropdown.Item as='a' href={PATHS.ADMIN_SETTINGS}>Settings</Dropdown.Item>
              )}
              <Dropdown.Item as='a' href={PATHS.LOGOUT}>Logout</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Menu.Menu>
      ) : (
        <>
          {isAdmin && (
            <Menu.Item as='a' href={PATHS.ADMIN_SETTINGS} active={location.pathname === PATHS.ADMIN_SETTINGS}>
              Settings
            </Menu.Item>
          )}
          <Menu.Item as='a' href={PATHS.LOGOUT}>Logout</Menu.Item>
        </>
      )}
  </>
  )
}

const DesktopContainer = ({ children }: { children: ReactNode }) => {
  const [fixed, setFixed] = useState(true);

  return (
    <Media greaterThan='mobile'>
      <InView
        threshold={1}
        onChange={() => setFixed(!fixed)}
      >
        <Segment
          inverted
          textAlign='center'
          vertical
        >
          <Menu
            fixed={fixed ? 'top' : undefined}
            inverted={!fixed}
            pointing={!fixed}
            secondary={!fixed}
            size='large'
          >
            <Container>
              <SharedMenu variant='desktop' />
            </Container>
          </Menu>
        </Segment>
      </InView>
      <br/>
      {children}
    </Media>
  )
}

const MobileContainer = ({ children }: {children: ReactNode}) => {
  const isTeacher = useIsTeacher();
  const [sidebarOpened, setSideBarOpened] = useState(false);

  if (isTeacher) {
    return (
      <Media at='mobile'> {children} </Media>
    )
  }

  return (
    <Media at='mobile'>
      <Sidebar.Pushable>
        <Sidebar
          style={{'top': '44px'}}
          className='mobile-sidebar x y z win'
          as={Menu}
          animation='overlay'
          direction='right'
          inverted
          onHide={() => setSideBarOpened(false)}
          vertical
          visible={sidebarOpened}
        >
          <SharedMenu variant='mobile' />
        </Sidebar>

        <Sidebar.Pusher dimmed={sidebarOpened}>
          <Segment
            inverted
            textAlign='center'
            vertical
            className='mobile-menu'
          >
            <Container>
              <Menu inverted pointing secondary size='large'>
                <Menu.Item position='right' onClick={() => setSideBarOpened(true)}>
                  <Icon name='sidebar' />
                </Menu.Item>
              </Menu>
            </Container>
          </Segment>

          {children}
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </Media>
  )
}

const ResponsiveContainer = ({ children }: {children: ReactNode}) => (
  <MediaContextProvider>
    <DesktopContainer>{children}</DesktopContainer>
    <MobileContainer>{children}</MobileContainer>
  </MediaContextProvider>
)

export { ResponsiveContainer }
